import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Container,
  Box,
  Typography,
  Grid,
  IconButton,
  Avatar,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  keyframes,
  CircularProgress,
  Card,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  People,
  CheckCircle,
  Star,
  Message,
  VideoCall,
  Logout,
  Settings,
  Notifications,
  CalendarToday,
  RateReview,
  PowerSettingsNew,
  Edit,
} from '@mui/icons-material';
import { logout } from '../../store/slices/authSlice';
import lawyerService from '../../services/lawyerService';
import OnboardingWizard from '../../components/Lawyer/OnboardingWizard';

import { useTranslation } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { axelionColors } from '../../theme/axelionTheme';
import NotificationCenter from '../../components/UI/NotificationCenter';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LawyerDashboardGlass = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [consultationRequests, setConsultationRequests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusOnline, setStatusOnline] = useState(true);
  const [onboardingDone, setOnboardingDone] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, consultationsData, requestsData, reviewsData] = await Promise.all([
        lawyerService.dashboard.getStats(),
        lawyerService.dashboard.getPendingConsultations(),
        lawyerService.consultation.getConsultationRequests('pending'),
        lawyerService.dashboard.getRecentReviews(3),
      ]);

      setStats(statsData);
      setConsultations(consultationsData);
      setConsultationRequests(requestsData);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await lawyerService.consultation.acceptConsultationRequest(requestId);
      toast.success('Запрос принят!');
      loadDashboardData(); // Reload data
    } catch (error) {
      toast.error('Ошибка при принятии запроса');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await lawyerService.consultation.rejectConsultationRequest(requestId);
      toast.success('Запрос отклонен');
      loadDashboardData(); // Reload data
    } catch (error) {
      toast.error('Ошибка при отклонении запроса');
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleStatusToggle = async () => {
    try {
      const newStatus = statusOnline ? 'offline' : 'online';
      await lawyerService.dashboard.updateStatus(newStatus);
      setStatusOnline(!statusOnline);
      toast.success(`Статус изменен на ${newStatus === 'online' ? 'В сети' : 'Не в сети'}`);
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleConfirmConsultation = async (consultationId) => {
    try {
      await lawyerService.schedule.confirmConsultation(consultationId);
      toast.success('Консультация подтверждена');
      loadDashboardData();
    } catch (error) {
      toast.error('Ошибка подтверждения');
    }
  };

  const handleStartConsultation = async (consultation) => {
    try {
      if (consultation.type === 'video') {
        navigate(`/consultations/video/${consultation.id}`);
      } else {
        navigate(`/consultations/chat/${consultation.id}`);
      }
    } catch (error) {
      toast.error('Ошибка запуска консультации');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('uz-UZ', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(amount) + ' сум';
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: axelionColors.bgCream,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: axelionColors.gold }} size={60} />
      </Box>
    );
  }

  // Показываем онбординг новым юристам (без кейсов и отзывов)
  const isNewLawyer = !onboardingDone && stats && stats.completedCases === 0 && stats.reviewsCount === 0;
  if (isNewLawyer) {
    return <OnboardingWizard onComplete={() => { setOnboardingDone(true); loadDashboardData(); }} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: axelionColors.bgCream,
        pb: 4,
      }}
    >
      {/* Clean Header */}
      <Box
        sx={{
          background: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: 3,
          px: 2,
          boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: axelionColors.bronze,
                  boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                  border: `1px solid ${axelionColors.borderLight}`,
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                }}
              >
                {user?.name?.charAt(0) || 'Ю'}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                    {user?.name || 'Юрист'}
                  </Typography>
                  <Chip
                    label={t('login.lawyer').toUpperCase()}
                    size="small"
                    sx={{
                      background: axelionColors.bronze,
                      color: 'white',
                      fontWeight: 'bold',
                      border: `1px solid ${axelionColors.borderLight}`,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    icon={<PowerSettingsNew sx={{ fontSize: 16, color: 'white !important' }} />}
                    label={statusOnline ? 'В сети' : 'Не в сети'}
                    size="small"
                    onClick={handleStatusToggle}
                    sx={{
                      background: statusOnline ? axelionColors.success : axelionColors.textMuted,
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: `1px solid ${axelionColors.borderLight}`,
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                  />
                  {stats?.rating > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Star sx={{ fontSize: 18, color: axelionColors.gold }} />
                      <Typography variant="body2" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                        {stats.rating}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <LanguageSwitcher
                variant="dropdown"
                sx={{
                  color: axelionColors.textDark,
                  bgcolor: axelionColors.bgCream,
                  '&:hover': { bgcolor: axelionColors.bgBeige },
                }}
              />
              <NotificationCenter sx={{
                background: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                color: axelionColors.textDark,
                '&:hover': { background: axelionColors.bgCream },
              }} />
              <IconButton
                title="Редактировать профиль"
                sx={{
                  background: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  color: axelionColors.gold,
                  '&:hover': { background: axelionColors.accentLight },
                }}
                onClick={() => navigate('/lawyer/profile/edit')}
              >
                <Edit />
              </IconButton>
              <IconButton
                sx={{
                  background: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  color: axelionColors.textDark,
                  '&:hover': {
                    background: axelionColors.bgCream,
                  },
                }}
                onClick={() => navigate('/settings')}
              >
                <Settings />
              </IconButton>
              <Button
                startIcon={<Logout sx={{ fontSize: 18 }} />}
                onClick={handleLogout}
                sx={{
                  backgroundColor: axelionColors.textDark,
                  color: '#FFFFFF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  px: 2.5,
                  py: 1,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#2D2D2D',
                    boxShadow: 'none',
                  },
                }}
              >
                {t('nav.logout')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              icon: <AttachMoney sx={{ fontSize: 40 }} />,
              label: 'Заработано за месяц',
              value: formatCurrency(stats?.monthlyEarnings || 0),
              iconBg: axelionColors.success,
            },
            {
              icon: <People sx={{ fontSize: 40 }} />,
              label: 'Активные клиенты',
              value: stats?.activeClients || 0,
              iconBg: axelionColors.gold,
            },
            {
              icon: <CheckCircle sx={{ fontSize: 40 }} />,
              label: 'Завершенные консультации',
              value: stats?.completedConsultations || 0,
              iconBg: axelionColors.bronze,
            },
            {
              icon: <TrendingUp sx={{ fontSize: 40 }} />,
              label: 'Общий доход',
              value: formatCurrency(stats?.totalEarnings || 0),
              iconBg: axelionColors.warning,
            },
          ].map((stat, index) => (
            <Grid
              item
              xs={6}
              sm={6}
              lg={3}
              key={index}
              sx={{
                animation: `${fadeInUp} 0.6s ease-out`,
                animationDelay: `${index * 0.1}s`,
                animationFillMode: 'both',
              }}
            >
              <Card
                sx={{
                  p: 2.5,
                  background: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(26, 26, 26, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '8px',
                      background: stat.iconBg,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: axelionColors.textMuted }} gutterBottom>
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                      {stat.value}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Incoming Consultation Requests */}
        {consultationRequests.length > 0 && (
          <Card
            sx={{
              p: 3,
              mb: 4,
              background: axelionColors.bgLight,
              border: `1px solid ${axelionColors.borderLight}`,
              borderRadius: '8px',
              boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                Входящие запросы на консультации
              </Typography>
              <Chip
                label={`${consultationRequests.length} новых`}
                sx={{
                  background: axelionColors.error,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </Box>

            <Grid container spacing={2}>
              {consultationRequests.map((request) => (
                <Grid item xs={12} key={request.id}>
                  <Card
                    sx={{
                      p: 3,
                      background: axelionColors.bgCream,
                      border: `1px solid ${axelionColors.borderLight}`,
                      borderRadius: '8px',
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(26, 26, 26, 0.1)',
                      },
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: axelionColors.gold,
                            }}
                          >
                            {request.client?.name?.charAt(0) || 'К'}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                              {request.client?.name || 'Клиент'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                              {new Date(request.createdAt).toLocaleDateString('ru-RU', {
                                day: 'numeric',
                                month: 'long',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: axelionColors.textDark, mb: 1 }}>
                            Вопрос:
                          </Typography>
                          <Typography variant="body2" sx={{ color: axelionColors.textDark, mb: 2 }}>
                            {request.question}
                          </Typography>

                          {request.description && (
                            <>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: axelionColors.textDark, mb: 1 }}>
                                Описание:
                              </Typography>
                              <Typography variant="body2" sx={{ color: axelionColors.textMuted, mb: 2 }}>
                                {request.description}
                              </Typography>
                            </>
                          )}

                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                              icon={request.consultationType === 'video' ? <VideoCall /> : <Message />}
                              label={request.consultationType === 'video' ? 'Видео консультация' : 'Чат консультация'}
                              size="small"
                              sx={{
                                background: request.consultationType === 'video' ? axelionColors.gold : axelionColors.bronze,
                                color: 'white',
                                border: `1px solid ${axelionColors.borderLight}`,
                              }}
                            />
                            <Chip
                              icon={<CalendarToday />}
                              label={`${new Date(request.preferredDate).toLocaleDateString('ru-RU')} в ${request.preferredTime}`}
                              size="small"
                              sx={{
                                background: axelionColors.bgLight,
                                color: axelionColors.textDark,
                                border: `1px solid ${axelionColors.borderLight}`,
                              }}
                            />
                            <Chip
                              icon={<AttachMoney />}
                              label={formatCurrency(request.price)}
                              size="small"
                              sx={{
                                background: axelionColors.success,
                                color: 'white',
                                border: `1px solid ${axelionColors.borderLight}`,
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            height: '100%',
                            justifyContent: 'center',
                          }}
                        >
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleAcceptRequest(request.id)}
                            sx={{
                              background: axelionColors.success,
                              color: 'white',
                              fontWeight: 'bold',
                              borderRadius: '8px',
                              '&:hover': {
                                background: '#16a34a',
                              },
                            }}
                          >
                            Принять запрос
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => handleRejectRequest(request.id)}
                            sx={{
                              color: axelionColors.error,
                              borderColor: axelionColors.error,
                              fontWeight: 'bold',
                              borderRadius: '8px',
                              '&:hover': {
                                background: axelionColors.errorLight,
                                borderColor: axelionColors.error,
                              },
                            }}
                          >
                            Отклонить
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Pending Consultations */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                p: 3,
                background: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                  Предстоящие консультации
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CalendarToday />}
                  onClick={() => navigate('/lawyer/schedule')}
                  sx={{
                    background: axelionColors.gold,
                    color: 'white',
                    borderRadius: '8px',
                    '&:hover': {
                      background: axelionColors.goldDark,
                    },
                  }}
                >
                  Календарь
                </Button>
              </Box>

              {consultations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CalendarToday sx={{ fontSize: 48, color: axelionColors.borderLight, mb: 2 }} />
                  <Typography variant="body1" sx={{ color: axelionColors.textDark, mb: 1 }}>
                    Нет предстоящих консультаций
                  </Typography>
                  <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                    Когда клиенты запишутся на консультацию, они появятся здесь
                  </Typography>
                </Box>
              ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: axelionColors.textDark, fontWeight: 'bold', borderBottom: `1px solid ${axelionColors.borderLight}` }}>Клиент</TableCell>
                      <TableCell sx={{ color: axelionColors.textDark, fontWeight: 'bold', borderBottom: `1px solid ${axelionColors.borderLight}` }}>Тема</TableCell>
                      <TableCell sx={{ color: axelionColors.textDark, fontWeight: 'bold', borderBottom: `1px solid ${axelionColors.borderLight}` }}>Дата и время</TableCell>
                      <TableCell sx={{ color: axelionColors.textDark, fontWeight: 'bold', borderBottom: `1px solid ${axelionColors.borderLight}` }}>Тип</TableCell>
                      <TableCell sx={{ color: axelionColors.textDark, fontWeight: 'bold', borderBottom: `1px solid ${axelionColors.borderLight}` }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consultations.map((consultation) => (
                      <TableRow
                        key={consultation.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: axelionColors.bgCream,
                          },
                        }}
                      >
                        <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: axelionColors.gold }}>
                              {consultation.clientName?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ color: axelionColors.textDark }}>
                              {consultation.clientName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                          <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                            {consultation.topic}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                          <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                            {consultation.date} в {consultation.time}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                          <Chip
                            icon={consultation.type === 'video' ? <VideoCall /> : <Message />}
                            label={consultation.type === 'video' ? 'Видео' : 'Чат'}
                            size="small"
                            sx={{
                              background: consultation.type === 'video' ? axelionColors.gold : axelionColors.bronze,
                              color: 'white',
                              border: `1px solid ${axelionColors.borderLight}`,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleStartConsultation(consultation)}
                            sx={{
                              background: axelionColors.success,
                              color: 'white',
                              borderRadius: '8px',
                              '&:hover': {
                                background: '#16a34a',
                              },
                            }}
                          >
                            Начать
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Card>
          </Grid>

          {/* Recent Reviews */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                p: 3,
                background: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark }} gutterBottom>
                Последние отзывы
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, flex: 1 }}>
                {reviews.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <RateReview sx={{ fontSize: 48, color: axelionColors.borderLight, mb: 2 }} />
                    <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                      Пока нет отзывов
                    </Typography>
                  </Box>
                ) : reviews.map((review) => (
                  <Card
                    key={review.id}
                    sx={{
                      p: 2,
                      background: axelionColors.bgCream,
                      border: `1px solid ${axelionColors.borderLight}`,
                      borderRadius: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                        {review.clientName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} sx={{ fontSize: 16, color: axelionColors.gold }} />
                        ))}
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: axelionColors.textMuted, mb: 1 }}>
                      {review.comment}
                    </Typography>
                    <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                      {review.date}
                    </Typography>
                  </Card>
                ))
                }
              </Box>

              <Button
                fullWidth
                variant="contained"
                startIcon={<RateReview />}
                sx={{
                  mt: 2,
                  background: axelionColors.gold,
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    background: axelionColors.goldDark,
                  },
                }}
                onClick={() => navigate('/lawyer/reviews')}
              >
                Смотреть все отзывы
              </Button>
            </Card>
          </Grid>

          {/* Interactive Analytics Dashboard */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Weekly Activity Chart */}
              <Grid item xs={12} md={8}>
                <Card
                  sx={{
                    p: 3,
                    background: axelionColors.bgLight,
                    border: `1px solid ${axelionColors.borderLight}`,
                    borderRadius: '8px',
                    boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark, mb: 3 }}>
                    Активность за неделю
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: 200 }}>
                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day, index) => {
                      const weeklyData = stats?.weeklyActivity || [0, 0, 0, 0, 0, 0, 0];
                      const maxVal = Math.max(...weeklyData, 1);
                      const heightPct = Math.max((weeklyData[index] / maxVal) * 100, 5);
                      const isMax = weeklyData[index] === maxVal && weeklyData[index] > 0;
                      return (
                        <Box
                          key={day}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: '80%',
                              height: `${heightPct}%`,
                              background: isMax ? axelionColors.gold : axelionColors.bgBeige,
                              borderRadius: '8px 8px 0 0',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': {
                                background: axelionColors.gold,
                                transform: 'scaleY(1.05)',
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{
                                position: 'absolute',
                                top: -25,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                color: axelionColors.textDark,
                              }}
                            >
                              {weeklyData[index]}
                            </Typography>
                          </Box>
                          <Typography variant="caption" fontWeight="bold" sx={{ color: axelionColors.textMuted }}>
                            {day}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              </Grid>

              {/* Performance Metrics Cards */}
              <Grid item xs={12} md={4}>
                <Grid container spacing={2}>
                  {stats?.responseRate > 0 ? (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 2.5,
                          background: `linear-gradient(135deg, ${axelionColors.gold} 0%, ${axelionColors.goldDark} 100%)`,
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(184, 149, 110, 0.25)',
                          color: 'white',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Скорость ответа
                            </Typography>
                            <Typography variant="h3" fontWeight="bold">
                              {stats.responseRate}%
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5 }}>
                              {stats?.responseRateChange ? `${stats.responseRateChange > 0 ? '+' : ''}${stats.responseRateChange}% от прошлой недели` : ''}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '8px',
                              background: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <TrendingUp sx={{ fontSize: 28 }} />
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ) : null}

                  {stats?.rating > 0 ? (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 2.5,
                          background: `linear-gradient(135deg, ${axelionColors.bronze} 0%, ${axelionColors.bronzeDark} 100%)`,
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(139, 115, 85, 0.25)',
                          color: 'white',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Рейтинг
                            </Typography>
                            <Typography variant="h3" fontWeight="bold">
                              {stats.rating}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.3, mt: 0.5 }}>
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} sx={{ fontSize: 16, opacity: i < Math.round(stats.rating) ? 1 : 0.3 }} />
                              ))}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '8px',
                              background: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Star sx={{ fontSize: 28 }} />
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ) : null}

                  {/* Onboarding card when no stats */}
                  {!stats?.responseRate && !stats?.rating && (
                    <Grid item xs={12}>
                      <Card
                        onClick={() => navigate('/lawyer/schedule')}
                        sx={{
                          p: 3,
                          background: `linear-gradient(135deg, ${axelionColors.gold} 0%, ${axelionColors.goldDark} 100%)`,
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(184, 149, 110, 0.25)',
                          color: 'white',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(184, 149, 110, 0.35)',
                          },
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                          Начните принимать заявки
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                          Заполните расписание, чтобы клиенты могли записаться на консультацию
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.5)',
                            '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                          }}
                        >
                          Настроить расписание
                        </Button>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

    </Box>
  );
};

export default LawyerDashboardGlass;
